// src/pages/FinancialsPage.js
// Updated: Tuesday, August 4, 2026 at 8:25 PM EDT

import React, { useState } from 'react';
import { 
  Container, Paper, Tabs, Tab, Box, Typography, Stack, Grid, 
  Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup 
} from '@mui/material';
import { 
  ExpandMore, Dashboard, MoveToInbox, Outbox, 
  AccountBalance, VolunteerActivism, Settings, 
  FactCheck, Assessment, AccountBalanceWallet, People, Gavel
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// --- COMPONENTS ---
import FundBalanceDashboard from '../components/financials/FundBalanceDashboard';

// Income Components
import BatchEntryForm from '../components/financials/BatchEntryForm';
import CSVImportParser from '../components/financials/CSVImportParser';
import GivingUnitsManager from '../components/financials/GivingUnitsManager'; 

// Expense Components
import ExpenseManager from '../components/financials/ExpenseManager';
import VendorManagement from '../components/financials/VendorManagement';
import ExpenseTrendAnalysis from '../components/financials/ExpenseTrendAnalysis';

// Banking & Ledger Components
import GeneralLedgerView from '../components/financials/GeneralLedgerView';
import BankReconciliation from '../components/financials/BankReconciliation';
import FundTransferForm from '../components/financials/FundTransferForm';
import AssetManager from '../components/financials/AssetManager';
import LiabilityManager from '../components/financials/LiabilityManager';

// Stewardship & Planning Components
import BudgetManager from '../components/financials/BudgetManager';
import ReportsCenter from '../components/financials/ReportsCenter'; 
import GivingAnalysisReport from '../components/financials/GivingAnalysisReport';
import LapsedDonorReport from '../components/financials/LapsedDonorReport';
import ContributionStatementGenerator from '../components/financials/ContributionStatementGenerator';
import DonorTaxStatements from '../components/financials/DonorTaxStatements';

// Configuration Components
import FundConfiguration from '../components/financials/FundConfiguration';
import DonationTypeConfiguration from '../components/financials/DonationTypeConfiguration'; 
import ChurchEntitySettings from '../components/financials/ChurchEntitySettings';
import CategorySettings from '../components/financials/CategorySettings';
import FinancialDataManager from '../components/financials/FinancialDataManager';
import FinancialAuditLog from '../components/financials/FinancialAuditLog';
import {
  canApproveFinancialData,
  canEnterFinancialData,
  canViewFinancialAudit,
  getFinanceRoleLabel,
} from '../utils/financePermissions';

// Helper for Section Headers
const SectionHeader = ({ title, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold', color: 'text.primary' }}>
            {title}
        </Typography>
    </Box>
);

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const FinancialsPage = () => {
  const { userProfile } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  
  const [stewardshipView, setStewardshipView] = useState('analysis');

  const isFinanceAdmin = canApproveFinancialData(userProfile);
  const canEnter = canEnterFinancialData(userProfile);
  const canViewAudit = canViewFinancialAudit(userProfile);

  const tabs = [
    { 
        label: "Dashboard", 
        icon: <Dashboard />,
        component: (
            <Stack spacing={3}>
                <FundBalanceDashboard />
            </Stack>
        ), 
        visible: true 
    },
    { 
        label: "Income & Donors", 
        icon: <MoveToInbox />,
        component: (
            <Stack spacing={2}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Contribution Batches" icon={<MoveToInbox color="primary" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        {canEnter ? <BatchEntryForm /> : (
                          <Typography color="text.secondary">Viewer access is read-only.</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Donor Database (Giving Units)" icon={<People color="action" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <GivingUnitsManager />
                    </AccordionDetails>
                </Accordion>
                {isFinanceAdmin && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <SectionHeader title="Import Digital Giving" icon={<FactCheck color="primary" />} />
                        </AccordionSummary>
                        <AccordionDetails>
                            <CSVImportParser />
                        </AccordionDetails>
                    </Accordion>
                )}
            </Stack>
        ), 
        visible: true 
    },
    { 
        label: "Expenses & Vendors", 
        icon: <Outbox />,
        component: (
            <Stack spacing={2}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Payables & Expenses" icon={<Outbox color="error" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ExpenseManager readOnly={!canEnter} />
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Vendor Database" icon={<People color="action" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <VendorManagement />
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Monthly Expense Trends" icon={<Assessment color="secondary" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ExpenseTrendAnalysis />
                    </AccordionDetails>
                </Accordion>
            </Stack>
        ), 
        visible: true 
    },
    { 
        label: "Banking & Ledger", 
        icon: <AccountBalance />,
        component: (
            <Stack spacing={2}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="General Ledger" icon={<FactCheck color="action" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <GeneralLedgerView />
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Bank Reconciliation" icon={<FactCheck color="success" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <BankReconciliation />
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Internal Fund Transfers" icon={<AccountBalanceWallet color="info" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        {isFinanceAdmin ? <FundTransferForm /> : (
                          <Typography color="text.secondary">Finance Administrator access is required.</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Assets & Liabilities" icon={<AccountBalanceWallet />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}><AssetManager /></Grid>
                            <Grid item xs={12} md={6}><LiabilityManager /></Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        ), 
        visible: true 
    },
    { 
        label: "Stewardship & Planning", 
        icon: <VolunteerActivism />,
        component: (
            <Stack spacing={2}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Annual Budgeting" icon={<AccountBalanceWallet />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <BudgetManager />
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Financial Reports Center" icon={<Assessment color="primary" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ReportsCenter />
                    </AccordionDetails>
                </Accordion>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                         <SectionHeader title="Stewardship Analysis & Tax Tools" icon={<VolunteerActivism color="secondary" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={stewardshipView}
                                    exclusive
                                    onChange={(e, val) => val && setStewardshipView(val)}
                                    size="medium"
                                >
                                    <ToggleButton value="analysis">Giving Analysis</ToggleButton>
                                    <ToggleButton value="lapsed">Lapsed Donors</ToggleButton>
                                    <ToggleButton value="statements">Bulk Tax Statements</ToggleButton>
                                    <ToggleButton value="individual">Individual Lookup</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Paper>
                        <Box sx={{ mt: 2 }}>
                            {stewardshipView === 'analysis' && <GivingAnalysisReport />}
                            {stewardshipView === 'lapsed' && <LapsedDonorReport />}
                            {stewardshipView === 'statements' && <ContributionStatementGenerator />}
                            {stewardshipView === 'individual' && <DonorTaxStatements />}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        ), 
        visible: true 
    },
    { 
        label: "Configuration", 
        icon: <Settings />,
        component: (
            <Stack spacing={2}>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Funds & Donation Types" icon={<Settings />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={7}>
                                <FundConfiguration />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <DonationTypeConfiguration />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
                
                {/* --- NEW SECTION: Expense Categories --- */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Expense Categories" icon={<Outbox color="error" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <CategorySettings />
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Financial Backup & Bulk Import" icon={<FactCheck color="primary" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <FinancialDataManager />
                    </AccordionDetails>
                </Accordion>

                {canViewAudit && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <SectionHeader title="Financial Audit History" icon={<FactCheck color="success" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                      <FinancialAuditLog />
                    </AccordionDetails>
                  </Accordion>
                )}

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SectionHeader title="Legal Entity Configuration" icon={<Gavel color="action" />} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ maxWidth: 600 }}>
                            <ChurchEntitySettings />
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        ), 
        visible: isFinanceAdmin 
    },
  ];

  const visibleTabs = tabs.filter(t => t.visible);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 0, borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
        <Box className="no-print" sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h4" fontWeight="bold">Financial Stewardship</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {getFinanceRoleLabel(userProfile)} access • Manage donations, expenses, and reports.
          </Typography>
        </Box>
        <Box className="no-print" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary">
            {visibleTabs.map((tab, index) => (
                <Tab key={index} label={tab.label} icon={tab.icon} iconPosition="start" sx={{ fontWeight: 'bold', py: 2, minHeight: 60 }} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ p: 3 }}>
            {visibleTabs.map((tab, index) => (
            <TabPanel key={index} value={tabValue} index={index}>
                {tab.component}
            </TabPanel>
            ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default FinancialsPage;